import {
	AuthInfoWithCnf,
	ClientInfoType,
	ENVIRONMENT,
	knownAADClientInfo,
} from '@citrite/msal-browser';
import { versionInfo } from '@citrite/workspace-ui-platform';
import { getFromLocalStorage } from 'javascript/sf/Storage';
import { AADSSOICAContents } from 'Workspace/BrowserExtension/aadSSOSpecificIcaFileParser';
import { lastUserDetailsKey } from 'Workspace/UserContext/constants';
import { UserDetailsCacheEntry } from 'Workspace/UserContext/UserContextLoader';

//This feild is sent to Chrome app from the Native client
// So that they can use if for the MSAL login hint
// Selecting a name which indicates WSP usage instead MSAL specific
export const userAuthAliasKeyForICAData = 'UserAuthAlias';

export const incompleteClientInfoError = (correlationId: string) => {
	const innerErrorObject = {
		errorCode: 'invalid_request',
		errorMessage:
			'Invalid or Incomplete client info obtained from Citrix Workspace Native App.',
		name: 'CtxLocalError',
		correlationId,
	};

	return {
		error: JSON.stringify(innerErrorObject),
		correlationId,
	};
};

export function wsuiAuthEnvironment() {
	const wsuiInfo = versionInfo.get();
	return wsuiInfo.environment === 'release' && wsuiInfo.release !== 'master'
		? ENVIRONMENT.PROD
		: ENVIRONMENT.NON_PROD;
}

/*
	This is required since native clients will pass back verbatim , the new AADSSO from the ICA file , 
	We need to convert these two field names to the correct field names in the AuthInfo object. 
	AzureSSOnResourceAppId   ---> WSUI has to convert to resourceAppId
	AzureSSOnAuthorizeUrl    ---> WSUI has to convert to authority

	The following other two fields from the ICA file, the CWA Native apps will convert and send to WSUI
	to preserve backward compatibility: 

	AzureSSOnClientId     ---> The CWA Native apps will convert to -----> clientId  
	AzureSSOnRedirectUri  ---> The CWA Native apps will convert to -----> redirectUri
*/
export function parseJsonFromNativeToAuthInfo(jsonPayload: string): AuthInfoWithCnf {
	const rawParsedAuthInfo: AuthInfoWithCnf &
		Pick<AADSSOICAContents, 'AzureSSOnResourceAppId' | 'AzureSSOnAuthorizeUrl'> =
		JSON.parse(jsonPayload);

	return {
		...rawParsedAuthInfo,
		authority: rawParsedAuthInfo.AzureSSOnAuthorizeUrl,
		resourceAppId: rawParsedAuthInfo.AzureSSOnResourceAppId,
	};
}

export function getUpnOrAuthAliasForCurrentUser(): string | undefined {
	try {
		const lastLoggedUserFromStorage =
			getFromLocalStorage<UserDetailsCacheEntry>(lastUserDetailsKey);
		return (
			lastLoggedUserFromStorage?.upn || lastLoggedUserFromStorage?.authAlias || undefined
		); // we don't want falsy strings like empty strings either
	} catch (error) {
		/** Ignore errors. Empty loginHint is fine */
	}

	return undefined;
}

const requiredClientInfoFields: (keyof ClientInfoType)[] = [
	'clientId',
	'authority',
	'redirectUri',
	'resourceAppId',
];

export function validateClientInfo(clientInfo: ClientInfoType): boolean {
	if (!clientInfo) {
		return false;
	}

	// All fields are required
	const hasMissingFields = requiredClientInfoFields.some(field => {
		const value = clientInfo[field];
		return typeof value !== 'string' || value.trim() === '';
	});

	//All required fields are non empty and clientID is not Microsoft Id
	return (
		!hasMissingFields &&
		clientInfo?.clientId !== knownAADClientInfo.MicrosoftSample.clientId
	);
}
